import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Button from 'sushiweb/Button';
import red from 'sushiweb/tokens/color/red';
import Plus from 'sushiweb/Icons/all/Plus';
import Layout from 'sushiweb/Layout/Layout';
import Spinner from 'sushiweb/Spinner/Spinner';
import Search from 'sushiweb/Icons/all/Search';
import Download from 'sushiweb/Icons/all/Download';
import Breadcrumb from 'sushiweb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import {
  exportCSV,
  getCurrentDate,
  includesOption,
  indexOfOption,
  searchWithName,
  validateEmployeeName,
} from '../../helpers';
import {
  BreadCrumbWrapper,
  DashboardWrapper,
  HeadWrapper,
  LayoutContainers,
  SearchContainerV2,
  SearchInput,
  SpinnerHolder,
  StatusFilterAndExportCSVContainer,
  TabHeadWrapper,
  TabWrapper,
  TableContainer,
} from '../common/styles';
import DetailsWrapper from '../common/components/DetailsWrapper/DetailsWrapper';
import ErrorPage from '../common/components/ErrorPage';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  employeeStatusEnum,
  statusLabels,
} from '../common/components/constants';
import DataTables from '../common/components/DataTables';
import { employeeForGroupHeadColumn } from '../common/components/DataTables/components/headColumns';
import AddEmployeesModal from '../Employees/GroupsTable/ActionComponents/AddEmployeesModal';
import StatusFilter from '../common/components/StatusFilter';
import {
  DASHBOARD_ROUTE,
  HOME_ROUTE,
  EMPLOYEES_ROUTE,
} from '../../constants/routes';
import ZTrackingClient from '../../jumbo/ZTrackingClient';
import { isEmployeeAdditionButtonDisabled } from '../../utility';

const GroupDetails = ({
  fetchingGroupDetails,
  fetchedGroupDetails,
  fetchingEmployeesForGroup,
  fetchedEmployeesForGroup,
  group,
  employeesData,
  getGroupDetails,
  getEmployeesForGroup,
  selectedWorkspace,
  viewOnlyMode,
  currentFlow,
  getAllCompanyMappedOffices,
}) => {
  const { group_name: name = '', program_name: programName = '' } = group;
  const keyFields = ['email', 'name'];
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const { groupId = '' } = useParams();
  const [search, onInputChange] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddEmployeeModalVisible, setIsAddEmployeeModalVisible] = useState(
    false,
  );
  const { employees = [] } = employeesData;
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [5],
      label: 'ACTIVE_ONBOARDED',
      disabled: true,
    },
    {
      value: [1],
      label: 'ADDED_ONBOARDING_PENDING',
      disabled: true,
    },
    {
      value: [2],
      label: 'OPTED_IN_ONBOARDING_INCOMPLETE',
      disabled: true,
    },
    {
      value: [3, 4],
      label: 'EXITED',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  let onboardingPendingEmployees = 0;
  let onboardedEmployees = 0;
  let employeeExited = 0;
  employees.forEach(employee => {
    if (
      employee.status === employeeStatusEnum.EXITED ||
      employee.status === employeeStatusEnum.REMOVED_EXITED
    )
      employeeExited += 1;
    if (
      employee.status === employeeStatusEnum.ADDED_ONBOARDING_PENDING ||
      employee.status === employeeStatusEnum.OPTED_IN_ONBOARDING_INCOMPLETE
    )
      onboardingPendingEmployees += 1;
    if (employee.status === employeeStatusEnum.ACTIVE_ONBOARDED)
      onboardedEmployees += 1;
  });

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'GROUP_DETAILS',
      component: 'TABLE_DATA',
    });
  }, []);

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id) {
      getGroupDetails({
        groupId,
        workspaceId: selectedWorkspace?.workspace_id ?? '',
      });
      getAllCompanyMappedOffices();
    }
  }, [selectedWorkspace]);

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getEmployeesForGroup({
        groupId,
        workspaceId: selectedWorkspace?.workspace_id ?? '',
      });
  }, [selectedWorkspace, currentFlow]);

  useEffect(() => {
    if (!fetchingEmployeesForGroup && fetchedEmployeesForGroup) {
      if (employees) {
        employees.sort((a, b) => new Date(b.added_on) - new Date(a.added_on));
        if (employees.length === 0) {
          setDisabledSearch(true);
        }
        if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
          for (let i = 0; i < employees.length; i += 1) {
            employees[i].status = employees[i].limit_status;
          }
        }
        setTableData(employees);
        setCurrentData(employees.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingEmployeesForGroup, currentFlow]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingEmployeesForGroup) {
        const arr = searchWithName(employees, search, keyFields);
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingEmployeesForGroup]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const refreshPage = () => {
    getGroupDetails({
      groupId,
      workspaceId: selectedWorkspace?.workspace_id ?? '',
    });
    getEmployeesForGroup({
      groupId,
      workspaceId: selectedWorkspace?.workspace_id ?? '',
    });
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'name_val', value: 'Name' },
      { key: 'email_val', value: 'Email ID' },
      { key: 'created_on_val', value: 'Created On' },
      { key: 'status_val', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.name_val = validateEmployeeName(data.name) ? data.name : ' ';
      labelledData.email_val = data.email;
      labelledData.created_on_val = data.created_on;
      labelledData.status_val = statusLabels[employeeStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `${name} - Employees - ${getCurrentDate()}`,
      headColumns,
    );
    const metaData = currentFilters.reduce((acc, filter, index) => {
      acc[`filter_${index}`] = String(filter);
      return acc;
    }, {});

    ZTrackingClient.recordClick({
      ui_source: 'GROUP_DETAILS',
      component: 'TEAM_EMPLOYEES_DOWNLOAD',
      meta_data: metaData,
    });
  };
  const downloadCSVStyle = {
    paddingLeft: '5px',
    paddingRight: '20px',
    cursor: 'pointer',
  };

  const closeAddEmployeeModal = () => {
    setIsAddEmployeeModalVisible(false);
    getGroupDetails({
      groupId,
      workspaceId: selectedWorkspace?.workspace_id ?? '',
    });
    getEmployeesForGroup({
      groupId,
      workspaceId: selectedWorkspace?.workspace_id ?? '',
    });
  };

  const addEmployeeBtnClick = () => {
    setIsAddEmployeeModalVisible(true);
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  const ActionComponent = () => {
    const isDisabled = isEmployeeAdditionButtonDisabled(
      selectedWorkspace,
      viewOnlyMode,
    );
    const button = (
      <div>
        <Button
          iconAlign='left'
          icon={<Plus />}
          onClick={addEmployeeBtnClick}
          disabled={isDisabled}
        >
          Add Employee
        </Button>
      </div>
    );
    return isDisabled ? (
      <Tooltip
        position='top'
        label='This feature is disabled, please contact your relationship manager'
        appearance='light'
        size='big'
        style={{
          marginTop: '-45px',
          fontSize: '12px',
          opacity: 0.8,
          marginRight: '10px',
          marginLeft: '0px',
          padding: '10px',
        }}
      >
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return fetchingGroupDetails ? (
    <DashboardWrapper>
      <SpinnerHolder className='center'>
        <Spinner color={red.z_red} size='medium' />
      </SpinnerHolder>
    </DashboardWrapper>
  ) : (
    <DashboardWrapper>
      {isAddEmployeeModalVisible && (
        <AddEmployeesModal
          group={group}
          isVisible={isAddEmployeeModalVisible}
          closeModal={closeAddEmployeeModal}
        />
      )}
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
          >
            Home
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${EMPLOYEES_ROUTE}`}
          >
            Employees
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
          >
            Team
          </Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <TabHeadWrapper>
        <H5>{name}</H5>
        <ActionComponent />
      </TabHeadWrapper>
      <LayoutContainers>
        <Layout gap={20}>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Meal plan'
              value={programName !== '' ? programName : ' - '}
            />
          </Layout.Item>
          {/* <Layout.Item colD={6} colT={6} colM={6}>
            <DetailsWrapper
              label='Budget per empoyee'
              value=' - '
            />
          </Layout.Item> */}
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Onboarded'
              value={`${onboardedEmployees} / ${employees.length}`}
            />
          </Layout.Item>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Onboarding pending'
              value={`${onboardingPendingEmployees} / ${employees.length}`}
            />
          </Layout.Item>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Exited'
              value={`${employeeExited} / ${employees.length}`}
            />
          </Layout.Item>
        </Layout>
      </LayoutContainers>
      <HeadWrapper>
        <H5>Employee(s)</H5>
        {' '}
      </HeadWrapper>
      {' '}
      <TabWrapper>
        {!fetchedGroupDetails ? (
          <TableContainer>
            <ErrorPage
              showAction
              actionLabel={REFRESH_LABEL}
              clickAction={refreshPage}
            />
          </TableContainer>
        ) : (
          <>
            <SearchContainerV2>
              <SearchInput
                leftIcon={<Search />}
                innerWidth='100%'
                value={search}
                onChange={onInputChange}
                onFocus={() => setIsSearchClicked(true)}
                onBlur={() => setIsSearchClicked(false)}
                label={
                  isSearchClicked || disabledSearch || search?.length ? (
                    'Search by employee email or name'
                  ) : (
                    <span style={{ fontSize: '16px', fontWeight: 300 }}>
                      {' '}
                      Search by employee email or name
                      {' '}
                    </span>
                  )
                }
                disabled={disabledSearch}
              />
              <StatusFilterAndExportCSVContainer>
                <StatusFilter
                  clearFilters={clearFilters}
                  currentFilters={currentFilters}
                  options={filterStatusOptions}
                  handleSelect={handleSelect}
                />
                <Download
                  color='grey'
                  onClick={exportSearchResultCSV}
                  style={downloadCSVStyle}
                />
              </StatusFilterAndExportCSVContainer>
            </SearchContainerV2>
            <TableContainer>
              <DataTables
                columns={employeeForGroupHeadColumn}
                loading={fetchingEmployeesForGroup || loading}
                data={currentData}
                pagination
                totalRows={searchResults ? searchResults.length : 0}
                handlePageChange={handlePageChange}
                onSort={handleSort}
                emptyStateTitle='No employees'
              />
            </TableContainer>
          </>
        )}
      </TabWrapper>
    </DashboardWrapper>
  );
};

GroupDetails.propTypes = {
  group: PropTypes.objectOf(PropTypes.any),
  employeesData: PropTypes.objectOf(PropTypes.any),
  getGroupDetails: PropTypes.func,
  getEmployeesForGroup: PropTypes.func,
  fetchingGroupDetails: PropTypes.bool,
  fetchedGroupDetails: PropTypes.bool,
  fetchingEmployeesForGroup: PropTypes.bool,
  fetchedEmployeesForGroup: PropTypes.bool,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  viewOnlyMode: PropTypes.bool,
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  getAllCompanyMappedOffices: PropTypes.func,
};

GroupDetails.defaultProps = {
  group: {},
  employeesData: {},
  getGroupDetails: _noop,
  getEmployeesForGroup: _noop,
  fetchingGroupDetails: true,
  fetchedGroupDetails: true,
  fetchingEmployeesForGroup: true,
  fetchedEmployeesForGroup: true,
  selectedWorkspace: {},
  viewOnlyMode: false,
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  getAllCompanyMappedOffices: {},
};

export default GroupDetails;
